import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Minus, Sparkles } from 'lucide-react';
import { useT } from '@/lib/i18n';

/**
 * FeatureComparison — factual capability matrix. No adversarial claims.
 *
 * Cells:
 *   full     — Check icon in acid green
 *   partial  — Minus icon in amber
 *   none     — X icon in zinc
 *   text     — arbitrary short string
 */

const ACC = '#D4FF00';

const rowGroups = [
  {
    titleFr: 'Détection & scoring',
    titleEn: 'Detection & scoring',
    rows: [
      { fr: 'AI Coach (recommandations personnalisées)', en: 'AI Coach (personalised feedback)',    hai: 'full', trad: 'none', basic: 'none' },
      { fr: 'Creator DNA (5 axes)',                       en: 'Creator DNA (5-axis)',                 hai: 'full', trad: 'none', basic: 'none' },
      { fr: 'AI Memory (apprentissage progressif)',       en: 'AI Memory (progressive learning)',     hai: 'full', trad: 'none', basic: 'none' },
      { fr: 'Hook / Emotion / Retention / Viral',         en: 'Hook / Emotion / Retention / Viral',   hai: 'full', trad: 'none', basic: 'partial' },
    ],
  },
  {
    titleFr: 'Production',
    titleEn: 'Production',
    rows: [
      { fr: 'Découpe automatique + reframe 9:16',        en: 'Auto cut + 9:16 reframe',              hai: 'full', trad: 'none', basic: 'full' },
      { fr: 'Sous-titres animés style CapCut',           en: 'Animated CapCut-style captions',       hai: 'full', trad: 'partial', basic: 'partial' },
      { fr: 'Traitement multi-langues',                  en: 'Multi-language processing',            hai: 'full', trad: 'partial', basic: 'partial' },
      { fr: 'Temps de traitement',                       en: 'Processing time',                      hai: '~4 min', trad: '~4 h', basic: '~10 min' },
    ],
  },
  {
    titleFr: 'Publication & croissance',
    titleEn: 'Publishing & growth',
    rows: [
      { fr: 'Publication multi-plateformes',              en: 'Multi-platform publishing',             hai: 'full', trad: 'partial', basic: 'partial' },
      { fr: 'Growth Dashboard (KPIs, objectifs)',         en: 'Growth Dashboard (KPIs, goals)',        hai: 'full', trad: 'none', basic: 'none' },
      { fr: 'Analytics par plateforme',                   en: 'Per-platform analytics',                hai: 'full', trad: 'none', basic: 'partial' },
      { fr: 'Prédictions de croissance',                  en: 'Growth predictions',                    hai: 'full', trad: 'none', basic: 'none' },
    ],
  },
];

const cellFor = (v) => {
  if (v === 'full')    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ background: `${ACC}18`, color: ACC }}><Check className="w-4 h-4" /></span>;
  if (v === 'partial') return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/15 text-amber-400"><Minus className="w-4 h-4" /></span>;
  if (v === 'none')    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-800 text-zinc-600"><X className="w-4 h-4" /></span>;
  return <span className="text-sm font-mono tabular-nums text-zinc-200">{v}</span>;
};

const FeatureComparison = () => {
  const { lang } = useT();
  return (
    <section id="comparison" className="py-24 px-6 lg:px-8" data-testid="landing-comparison">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5 font-mono">
            {lang === 'fr' ? 'Comparaison' : 'Comparison'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
            {lang === 'fr' ? 'Ce que HighlightAI apporte en plus' : 'What HighlightAI adds on top'}
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm sm:text-base">
            {lang === 'fr' ? 'Vue factuelle des capacités — pas de dénigrement, juste les faits.' : 'Factual capability view — no bashing, just facts.'}
          </p>
        </div>

        <div className="rounded-2xl border border-white/8 bg-white/[0.015] overflow-hidden" data-testid="comparison-table">
          <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-0 text-left">
            <div className="p-4 text-[10px] uppercase tracking-widest font-mono text-zinc-500 border-b border-white/5" />
            <div className="p-4 border-b border-l border-white/5 flex flex-col items-center justify-center">
              <div className="inline-flex items-center gap-1.5 mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF00]" />
                <span className="text-sm font-bold text-[#D4FF00]">HighlightAI</span>
              </div>
              <span className="text-[9px] uppercase tracking-widest font-mono text-zinc-500 flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> AI-native
              </span>
            </div>
            <div className="p-4 border-b border-l border-white/5 text-center">
              <div className="text-sm font-semibold text-zinc-300 mb-1">{lang === 'fr' ? 'Montage traditionnel' : 'Traditional editing'}</div>
              <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Premiere · CapCut · manual</div>
            </div>
            <div className="p-4 border-b border-l border-white/5 text-center">
              <div className="text-sm font-semibold text-zinc-300 mb-1">{lang === 'fr' ? 'Clip AI basique' : 'Basic clip AI'}</div>
              <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">{lang === 'fr' ? 'Découpe uniquement' : 'Cutting only'}</div>
            </div>
          </div>

          {rowGroups.map((g, gi) => (
            <div key={gi} data-testid={`comparison-group-${gi}`}>
              <div className="grid grid-cols-[1.6fr_3fr] items-center px-4 py-2 bg-white/[0.02] border-b border-white/5">
                <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
                  {lang === 'fr' ? g.titleFr : g.titleEn}
                </div>
              </div>
              {g.rows.map((row, ri) => (
                <motion.div
                  key={ri}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.3, delay: ri * 0.03 }}
                  className="grid grid-cols-[1.6fr_1fr_1fr_1fr] items-center border-b border-white/5 last:border-b-0 hover:bg-white/[0.01]"
                  data-testid={`comparison-row-${gi}-${ri}`}
                >
                  <div className="p-4 text-sm text-zinc-200">{lang === 'fr' ? row.fr : row.en}</div>
                  <div className="p-4 border-l border-white/5 flex justify-center">{cellFor(row.hai)}</div>
                  <div className="p-4 border-l border-white/5 flex justify-center">{cellFor(row.trad)}</div>
                  <div className="p-4 border-l border-white/5 flex justify-center">{cellFor(row.basic)}</div>
                </motion.div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureComparison;
